import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../config/uiLayoutConfig";
import { usePageLayout } from "../context/LayoutContext";
import { useUser } from "../pages/login/UserContextPersistent";
import { useFilteredNavData } from "../components/navbar/NewNavData";
import {
  buildFleetNavFromLayout,
  ensureFleetAttendantNavItem,
  createHasPermission,
  filterNavForInactiveOrganization,
  filterNavForOrganizationPath,
  injectRouteEyeSuperAdminNavItems,
} from "../layout/navLayoutUtils";
import { useNavActionHandlers } from "./useNavActionHandlers";

export function useFleetNavLayout() {
  const location = useLocation();
  const { state, organizationInactive } = useUser();
  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const legacyNavData = useFilteredNavData(location.pathname);
  const { resolveAction } = useNavActionHandlers();
  const { layout: navLayout } = usePageLayout(useJsonLayout ? PAGE_IDS.FLEET_NAV : null);
  const currentUser = state?.user || {};
  const roleName = String(
    currentUser?.role?.name || currentUser?.role || state?.role?.name || state?.role || "",
  ).toLowerCase();
  const roleId =
    currentUser?.roleId || state?.roleId || currentUser?.role?.id || state?.role?.id;
  const isSuperAdmin =
    currentUser?.email === "sales@routeeye.io" ||
    Number(roleId) === 3 ||
    roleName.includes("superadmin") ||
    roleName.includes("super admin");
  const roleKey = isSuperAdmin ? "SUPER_ADMIN" : "ORG_ADMIN";

  const hasPermission = useMemo(
    () =>
      createHasPermission(state?.features, {
        writesDisabled: organizationInactive,
      }),
    [state?.features, organizationInactive],
  );

  const jsonNavData = useMemo(() => {
    if (!useJsonLayout || !navLayout) {
      return null;
    }

    const built = buildFleetNavFromLayout(ensureFleetAttendantNavItem(navLayout), {
      hasPermission,
      resolveAction,
      roleKey,
    });

    const filtered = filterNavForOrganizationPath(
      built,
      location.pathname,
      hasPermission,
    );

    return filterNavForInactiveOrganization(
      injectRouteEyeSuperAdminNavItems(filtered, location.pathname),
      organizationInactive,
    );
  }, [
    useJsonLayout,
    navLayout,
    hasPermission,
    resolveAction,
    location.pathname,
    roleKey,
    organizationInactive,
  ]);

  const visibleLegacyNavData = useMemo(
    () => filterNavForInactiveOrganization(legacyNavData, organizationInactive),
    [legacyNavData, organizationInactive],
  );

  return useJsonLayout ? jsonNavData ?? [] : visibleLegacyNavData;
}
